import React, { useEffect, useState } from "react";
import Button from "../../components/common/Button";
import SplitButton from "../../components/common/SplitButton";
import { CircularArrow, Loading } from "../../components/icons";
import { formatPrice } from "../../utils/Utils";
import {
  FixedBottomBar,
  TotalWrapper,
  ArrowWrap,
  Total,
} from "../reservation-shared-styles";
import { useReservationResources } from "./hooks/useReservationResources";

interface ReservationBottomBarProps {
  isOrderSummaryOpen: boolean;
  onToggleOrderSummary: () => void;
  total: number;
  paidAmount?: number;
  currency: string;
  isDisabled: boolean;
  isArrowDisabled?: boolean;
  hasSecondaryPaymentOptions: boolean;
  isSubmittingReservation: boolean;
  onPayNow: () => void;
  onOpenPaymentModal: () => void;
}

// Hide the fixed bottom bar when the soft keyboard is open to prevent it from
// floating above the keyboard on iOS/Android. visualViewport.height shrinks when
// the keyboard opens; window.innerHeight does not, so comparing the two is reliable.
function useIsKeyboardOpen() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const check = () => setIsOpen(vv.height < window.innerHeight * 0.75);
    vv.addEventListener("resize", check);
    check();
    return () => vv.removeEventListener("resize", check);
  }, []);

  return isOpen;
}

export function ReservationBottomBar({
  isOrderSummaryOpen,
  onToggleOrderSummary,
  total,
  paidAmount = 0,
  currency,
  isDisabled,
  isArrowDisabled,
  hasSecondaryPaymentOptions,
  isSubmittingReservation,
  onPayNow,
  onOpenPaymentModal,
}: ReservationBottomBarProps) {
  const r = useReservationResources();
  const isKeyboardOpen = useIsKeyboardOpen();
  const amountDue = Math.max(0, total - paidAmount);

  const barRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => {
      if (el.offsetHeight === 0) return;
      document.documentElement.style.setProperty("--bottom-bar-height", `${el.offsetHeight}px`);
    };
    update();
    // Watch the border box, since --bottom-bar-height is offsetHeight (padding included);
    // a default content-box observer would miss padding-only changes.
    const observer = new ResizeObserver(update);
    observer.observe(el, { box: "border-box" });
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--bottom-bar-height");
    };
  }, [isKeyboardOpen]);
  
  if (isKeyboardOpen) return null;

  return (
    <FixedBottomBar id="bottom-bar" ref={barRef}>
      <TotalWrapper>
        <ArrowWrap
          id="view-order-btn"
          type="button"
          onClick={onToggleOrderSummary}
          aria-label="View order summary"
          disabled={isArrowDisabled}
        >
          <CircularArrow isOpen={isOrderSummaryOpen} disabled={isArrowDisabled} />
        </ArrowWrap>
        <Total id="total-price">{formatPrice(amountDue, currency)}</Total>
      </TotalWrapper>
      {hasSecondaryPaymentOptions ? (
        <SplitButton
          text={r.Payment}
          onMainClick={onPayNow}
          onChevronClick={onOpenPaymentModal}
          disabled={isDisabled || isSubmittingReservation}
          isSubmitting={isSubmittingReservation}
        />
      ) : (
        <Button
          id="bottom-bar-btn"
          type="button"
          variant="primary"
          md
          text={total === 0 ? r.Continue : r.PayNow}
          height="40px"
          fullWidth={false}
          onClick={onPayNow}
          disabled={isDisabled}
          iconComponent={isSubmittingReservation ? <Loading /> : undefined}
        />
      )}
    </FixedBottomBar>
  );
}